import { type ReactElement, useEffect, useMemo, useState } from "react";

import {
  APIError,
  api,
  type Document,
  type Organization,
  type Project,
} from "../api/client";

interface ResourceState<T> {
  items: T[];
  loading: boolean;
  error: APIError | Error | null;
}

const initialState = { items: [], loading: true, error: null };

function messageFor(error: APIError | Error): string {
  if (error instanceof APIError && error.code === "not_found") {
    return "This workspace could not be found or you no longer have access.";
  }
  if (error instanceof APIError) {
    return `${error.message} · Request ${error.requestId}`;
  }
  return error.message;
}

export function Documents({
  organizationId,
  projectId,
}: {
  organizationId?: string;
  projectId?: string;
}): ReactElement {
  const [organizations, setOrganizations] =
    useState<ResourceState<Organization>>(initialState);
  const [projects, setProjects] =
    useState<ResourceState<Project>>(initialState);
  const [documents, setDocuments] =
    useState<ResourceState<Document>>(initialState);

  useEffect(() => {
    let active = true;
    setOrganizations(initialState);
    api
      .organizations()
      .then((page) => {
        if (active)
          setOrganizations({ items: page.items, loading: false, error: null });
      })
      .catch((error: APIError | Error) => {
        if (active) setOrganizations({ items: [], loading: false, error });
      });
    return () => {
      active = false;
    };
  }, []);

  const selectedOrganizationId = organizationId ?? organizations.items[0]?.id;

  useEffect(() => {
    if (!selectedOrganizationId) {
      setProjects({ items: [], loading: false, error: null });
      return;
    }
    let active = true;
    setProjects(initialState);
    api
      .projects(selectedOrganizationId)
      .then((page) => {
        if (active)
          setProjects({ items: page.items, loading: false, error: null });
      })
      .catch((error: APIError | Error) => {
        if (active) setProjects({ items: [], loading: false, error });
      });
    return () => {
      active = false;
    };
  }, [selectedOrganizationId]);

  const selectedProjectId = projectId ?? projects.items[0]?.id;

  useEffect(() => {
    if (!selectedProjectId) {
      setDocuments({ items: [], loading: false, error: null });
      return;
    }
    let active = true;
    setDocuments(initialState);
    api
      .documents(selectedProjectId)
      .then((page) => {
        if (active)
          setDocuments({ items: page.items, loading: false, error: null });
      })
      .catch((error: APIError | Error) => {
        if (active) setDocuments({ items: [], loading: false, error });
      });
    return () => {
      active = false;
    };
  }, [selectedProjectId]);

  const selectedOrganization = useMemo(
    () =>
      organizations.items.find(
        (organization) => organization.id === selectedOrganizationId,
      ),
    [organizations.items, selectedOrganizationId],
  );
  const selectedProject = useMemo(
    () => projects.items.find((project) => project.id === selectedProjectId),
    [projects.items, selectedProjectId],
  );
  const firstError = organizations.error ?? projects.error ?? documents.error;

  function chooseOrganization(nextOrganizationId: string): void {
    window.location.assign(
      `/workspace/organizations/${encodeURIComponent(nextOrganizationId)}`,
    );
  }

  function chooseProject(nextProjectId: string): void {
    if (selectedOrganizationId) {
      window.location.assign(
        `/workspace/organizations/${encodeURIComponent(selectedOrganizationId)}/projects/${encodeURIComponent(nextProjectId)}`,
      );
    }
  }

  return (
    <main className="page page-documents">
      <header className="page-heading">
        <div>
          <p className="eyebrow">Team library</p>
          <h1>Documents</h1>
          <p>
            Browse the current state, immutable history, and edit ownership of
            every model.
          </p>
        </div>
        {selectedOrganizationId && (
          <div className="page-actions">
            <a
              className="button button-primary"
              href={`/workspace/teams/${encodeURIComponent(selectedOrganizationId)}`}
            >
              Manage team
            </a>
            <a
              className="button button-quiet"
              href={`/workspace/organizations/${encodeURIComponent(selectedOrganizationId)}/audit`}
            >
              View audit trail
            </a>
            <a className="button button-quiet" href="/explore">
              Explore public models
            </a>
          </div>
        )}
      </header>

      {firstError && (
        <section className="empty-state error-state" role="alert">
          <span className="status-orb status-danger" />
          <h2>We couldn’t open this workspace</h2>
          <p>{messageFor(firstError)}</p>
        </section>
      )}

      {!firstError && organizations.loading && (
        <LoadingCards label="Loading workspaces" />
      )}

      {!firstError &&
        !organizations.loading &&
        organizations.items.length === 0 && (
          <section className="empty-state">
            <p className="eyebrow">No memberships</p>
            <h2>Your team list is empty.</h2>
            <p>
              Open Teams to create one or join through a secure invitation link.
            </p>
            <a className="button button-primary" href="/workspace/teams">
              Open Teams
            </a>
          </section>
        )}

      {!firstError && organizations.items.length > 0 && (
        <>
          <section
            className="workspace-switcher"
            aria-label="Workspace selection"
          >
            <label>
              <span>Team</span>
              <select
                aria-label="Team"
                value={selectedOrganizationId}
                onChange={(event) => chooseOrganization(event.target.value)}
              >
                {organizations.items.map((organization) => (
                  <option key={organization.id} value={organization.id}>
                    {organization.name} · {organization.actor_role}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Project</span>
              <select
                aria-label="Project"
                disabled={projects.loading || projects.items.length === 0}
                value={selectedProjectId ?? ""}
                onChange={(event) => chooseProject(event.target.value)}
              >
                {projects.items.length === 0 && (
                  <option value="">No projects</option>
                )}
                {projects.items.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="workspace-context">
              <span
                className={`role-badge role-${selectedOrganization?.actor_role ?? "viewer"}`}
              >
                {selectedOrganization?.actor_role ?? "viewer"}
              </span>
              <small>
                {selectedProject?.description || "Shared VibeCAD workspace"}
              </small>
            </div>
          </section>

          {documents.loading ? (
            <LoadingCards label="Loading documents" />
          ) : documents.items.length === 0 ? (
            <section className="empty-state">
              <p className="eyebrow">Project ready</p>
              <h2>No documents have been registered yet.</h2>
              <p>
                Create the first document from VibeCAD to begin immutable
                version history.
              </p>
            </section>
          ) : (
            <section className="document-grid" aria-label="Documents">
              {documents.items.map((document) => (
                <DocumentCard key={document.id} document={document} />
              ))}
            </section>
          )}
        </>
      )}
    </main>
  );
}

function DocumentCard({ document }: { document: Document }): ReactElement {
  const checkout = document.checkout;
  const version = document.current_version;
  const state = checkout?.state ?? "available";
  const updatedAt = version?.created_at ?? document.created_at;

  return (
    <a
      className="document-card"
      href={`/workspace/documents/${encodeURIComponent(document.id)}`}
    >
      <div className="document-card-topline">
        <span className="file-glyph" aria-hidden="true">
          ◇
        </span>
        <span className={`lease-state lease-${state}`}>
          <span className="status-orb" />
          {state === "active"
            ? `Checked out by ${checkout?.owner.display_name}`
            : state}
        </span>
      </div>
      <div>
        <h2>{document.name}</h2>
        <p className="document-path">{document.relative_path}</p>
      </div>
      <dl className="document-meta">
        <div>
          <dt>Version</dt>
          <dd>{version ? `v${version.sequence}` : "Unversioned"}</dd>
        </div>
        <div>
          <dt>Size</dt>
          <dd>{version ? formatBytes(version.size_bytes) : "—"}</dd>
        </div>
        <div>
          <dt>Updated</dt>
          <dd>{formatRelativeDate(updatedAt)}</dd>
        </div>
      </dl>
      <span className="card-link">
        Open history <span aria-hidden="true">→</span>
      </span>
    </a>
  );
}

function LoadingCards({ label }: { label: string }): ReactElement {
  return (
    <section className="document-grid" aria-label={label} aria-busy="true">
      {[0, 1, 2].map((value) => (
        <div className="document-card skeleton" key={value} />
      ))}
    </section>
  );
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = units[0];
  for (let index = 1; index < units.length && value >= 1024; index += 1) {
    value /= 1024;
    unit = units[index];
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${unit}`;
}

function formatRelativeDate(value: string): string {
  const date = new Date(value);
  const difference = Date.now() - date.getTime();
  const days = Math.floor(difference / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
